import { forwardRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { Image } from '@hivebrite/design-system/Image';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import {
  linkControlledHoverEffect,
  primaryShadow,
} from '@hivebrite/design-system/Theme';
import { onEnterOrSpacePress } from '@hivebrite/utils/src/common/onEnterPress';

import { AttendeesRow } from 'hb-react/shared/apps/Events/components/AttendeesRow';
import EventDate from 'hb-react/shared/components/EventDate';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

import { type Event } from './types';
import {
  Body,
  Calendar,
  Cancelled,
  Categories,
  Header,
  Location,
  OnlineEvent,
  Owner,
  PricingTag,
  Title,
} from './ui';

export const EventCard = forwardRef<
  HTMLDivElement,
  {
    event: Event;
    hoverStyle?: string | null;
    onClick?: (event: Event) => void;
    selected?: boolean;
    timezoneConfiguration?: TimeZoneConfiguration;
  }
>(({ event, onClick, selected, hoverStyle, timezoneConfiguration }, ref) => {
  const handleClick = () => onClick?.(event);

  return (
    <StyledCard
      onClick={handleClick}
      onKeyDown={onEnterOrSpacePress(handleClick)}
      $selected={selected}
      $hoverStyle={hoverStyle}
      ref={ref}
      data-testid="event-card"
    >
      <Header>
        {event.cancelled ? (
          <Cancelled event={event} />
        ) : (
          <PricingTag event={event} />
        )}
        <Image
          src={event.coverPicture}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            aspectRatio: 1352 / 698,
          }}
          alt=""
          role="presentation"
        />

        <Calendar
          startDate={event.startDate}
          timezoneConfiguration={timezoneConfiguration}
          eventTimezone={event.timezone}
        />
      </Header>
      <Body>
        <Box flexDirection="column" gridRowGap={2}>
          <Title tabIndex={0}>
            <ScreenReadersContent>
              {s__('EventsAccessibility|Navigate to')}
            </ScreenReadersContent>
            <Dotdotdot clamp={2}>{event.title}</Dotdotdot>
          </Title>
          <Owner event={event} />
          <Box flexDirection="column" gridRowGap={1}>
            <EventDate
              event={{
                start_date: event.startDate,
                end_date: event.endDate,
                timezone: event.timezone,
              }}
              timezoneConfiguration={timezoneConfiguration}
            />
            <Location event={event} />
            <OnlineEvent event={event} />
          </Box>
          {event.hasAttendees && (
            <AttendeesRow
              event={{
                has_attendees: event.hasAttendees,
                id: event.id,
                type: event.type,
              }}
              mt={0}
              imgSize={32}
            />
          )}
        </Box>
        <Categories event={event} />
      </Body>
    </StyledCard>
  );
});

const StyledCard = styled(Card)<{
  $hoverStyle?: string | null;
  $selected?: boolean;
}>`
  display: flex;
  ${primaryShadow}
  flex-direction: column;
  overflow: hidden;
  outline: ${({ theme, $selected }) =>
    $selected ? `2px solid ${theme.colors.primary} !important` : 'none'};
  height: 100%;
  padding: 0;
  transition: box-shadow 0.2s ease-in-out;
  &:hover {
    ${({ $hoverStyle }) => $hoverStyle}
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  &:hover ${Title} {
    ${linkControlledHoverEffect()}
  }
  & ${Header} img {
    transition: transform 0.3s;
    transform: scale(1);
  }
  &:hover ${Header} img {
    transform: scale(1.05);
  }
`;
